import { type ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Check, Smile } from 'lucide-react';
import { cn } from '@/lib/utils';
import { STEP_HEADING } from '../components/fileCardStyles';
import WizardFooter from '../components/WizardFooter';
import singlePieceArt from '@/assets/upload-v2/single-piece.webp';
import multiplePiecesArt from '@/assets/upload-v2/multiple-pieces.webp';
import { selectV2, setMode, setStep } from '../v2Upload.slice';
import { V2UploadState } from '../v2Upload.d';

type Mode = V2UploadState['mode'];

function SingleArt() {
  return (
    <img
      src={singlePieceArt}
      alt="One board becoming a single carved tray"
      className="max-h-[136px] w-auto max-w-full object-contain"
    />
  );
}

function MultipleArt() {
  return (
    <img
      src={multiplePiecesArt}
      alt="A layered cookie and a lidded box, each built from several boards"
      className="max-h-[188px] w-auto max-w-full object-contain"
    />
  );
}

const TYPES: Array<{
  value: Mode;
  name: string;
  boards: string;
  blurb: string;
  example: string;
  Art: () => ReactElement;
}> = [
  {
    value: 'single',
    name: 'Single Piece',
    boards: 'One board',
    blurb: 'Everything comes from one board.',
    example: 'Example: one carved tray',
    Art: SingleArt,
  },
  {
    value: 'multi',
    name: 'Multiple Pieces',
    boards: 'More than one board',
    blurb: 'Your project is made from 2 or more pieces.',
    example: 'Example: layered cookie or box',
    Art: MultipleArt,
  },
];

function TypeCard({
  picked,
  locked,
  onPick,
  type,
}: {
  picked: boolean;
  locked: boolean;
  onPick: () => void;
  type: (typeof TYPES)[number];
}) {
  const { Art } = type;
  return (
    <button
      type="button"
      aria-label={type.name}
      aria-pressed={picked}
      disabled={locked}
      onClick={onPick}
      className={cn(
        'relative w-full rounded-xl border p-5 text-left transition-[border-color,box-shadow,background-color] duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        picked ? 'border-primary bg-accent/40' : 'border-border bg-card',
        locked
          ? 'cursor-not-allowed opacity-60'
          : 'hover:border-primary hover:shadow-md'
      )}
    >
      {picked && (
        <Check className="absolute right-4 top-4 h-5 w-5 rounded-full bg-primary p-0.5 text-primary-foreground" />
      )}
      <div className="flex flex-col items-start gap-5 md:flex-row md:items-center">
        <div className="min-w-0 md:w-[44%]">
          <h3 className="font-primary text-2xl font-bold leading-tight text-heading">
            {type.name}
          </h3>
          <p className="mt-1 font-primary text-base font-semibold text-primary">
            {type.boards}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">{type.blurb}</p>
          <p className="mt-2 text-xs italic text-muted-foreground">
            {type.example}
          </p>
        </div>
        <div className="flex w-full flex-1 justify-center md:w-auto">
          <Art />
        </div>
      </div>
    </button>
  );
}

export default function ChooseTypeStep() {
  const dispatch = useDispatch();
  const { mode, postId, groupId } = useSelector(selectV2);
  const locked = Boolean(postId ?? groupId);

  const pick = (value: Mode) => {
    dispatch(setMode(value));
    dispatch(setStep(1));
  };

  return (
    <div className="flex min-h-full flex-col">
      <h2 className={STEP_HEADING}>Choose your project type</h2>
      <p className="mb-4 max-w-[640px] text-sm text-muted-foreground">
        Pick the picture that matches your project.
      </p>

      <div className="mb-4 flex max-w-[1400px] items-start gap-2.5 rounded-xl border border-primary/25 bg-accent px-4 py-2.5 text-sm">
        <Smile className="mt-0.5 h-4 w-4 flex-none text-primary" />
        <p>
          <span className="font-medium">One board?</span> Choose Single.{' '}
          <span className="font-medium">More than one board?</span> Choose
          Multiple.
        </p>
      </div>

      <div data-focus="listing-type" className="max-w-[1400px] space-y-4">
        {TYPES.map((type) => (
          <TypeCard
            key={type.value}
            type={type}
            picked={mode === type.value}
            locked={locked}
            onPick={() => pick(type.value)}
          />
        ))}
      </div>

      {locked && (
        <p className="mt-3 text-xs text-muted-foreground">
          Your project has started, so its type is set. On a multi-workpiece
          project you can still finish after one piece.
        </p>
      )}

      <WizardFooter
        onContinue={() => dispatch(setStep(1))}
        busy={false}
        busyLabel=""
        backDisabled={false}
        continueDisabled={false}
      />
    </div>
  );
}
